import { Caption, Description } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import React from 'react'

import CardCasavoLogo from '@/components/CardCasavoLogo'
import { cardCityAreaDivider } from '@/components/ListingCard/styles.css'
import { useCities } from '@/context/Cities'
import { marginRight } from '@/shared-styles/styles.css'
import { CasavoInventory } from '@/types/listing'
import { CardDataListing } from '@/types/property-card'
import { propertyFloorFormatter, propertyFloorSuffix } from '@/utils/floors'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'

import Card from '../Card'
import { FavoriteButtonGtmTouchpoint } from '../FavouriteButton'

import { listingCardVerticalWrapper } from './styles.css'

type ListingCardVerticalProps =
  | {
      cardData: CardDataListing
      favoriteButtonGtmTouchpoint: FavoriteButtonGtmTouchpoint
      favouriteClick?: (listingId: string) => void
      favouriteEnabled: true
      onCardClick?: () => void
    }
  | {
      cardData: CardDataListing
      favoriteButtonGtmTouchpoint?: never
      favouriteClick?: (listingId: string) => void
      favouriteEnabled?: false
      onCardClick?: () => void
    }

export const ListingCardVertical: React.FC<ListingCardVerticalProps> = ({
  cardData,
  favoriteButtonGtmTouchpoint,
  favouriteClick,
  favouriteEnabled = false,
  onCardClick,
  ...props
}) => {
  const { lang, t } = useTranslation()
  const cities = useCities()

  const listingCity = cities.list.find((city) => city.slug === cardData.city)

  return (
    <div className={listingCardVerticalWrapper} data-testid="listing-list" onClick={onCardClick} {...props}>
      <Card
        badge={{
          left: cardData.ownedByCasavo && (
            <CardCasavoLogo inventory={cardData.inventory as CasavoInventory} isLabelVisible={true} />
          ),
        }}
        cardData={cardData}
        details={[
          <>
            <Description as="span">{cardData.commercialArea}</Description>{' '}
            <Caption as="span" color="greyscale500">
              m&sup2;
            </Caption>
          </>,
          <>
            <Description as="span" className={marginRight.s}>
              {propertyFloorFormatter(
                {
                  buildingFloors: cardData.buildingFloors,
                  entireBuilding: cardData.entireBuilding,
                  entranceFloor: cardData.entranceFloor,
                  floorPosition: cardData.floorPosition,
                },
                t
              )}
            </Description>
            <Caption as="span" color="greyscale500">
              {propertyFloorSuffix(
                {
                  buildingFloors: cardData.buildingFloors,
                  entireBuilding: cardData.entireBuilding,
                  entranceFloor: cardData.entranceFloor,
                  floorPosition: cardData.floorPosition,
                },
                t
              )}
            </Caption>
          </>,
        ]}
        favoriteButtonGtmTouchpoint={favoriteButtonGtmTouchpoint}
        favouriteClick={favouriteClick}
        favouriteEnabled={favouriteEnabled}
        hasOutdoorSpace={cardData.hasOutdoorSpace}
        headline={
          <>
            {listingCity?.translatedCityName && (
              <>
                <span>{listingCity?.translatedCityName}</span>
                {cardData.zone && (
                  <>
                    <span className={cardCityAreaDivider} />
                    <span data-testid="listing-card-zone">{cardData.zone.label}</span>
                  </>
                )}
              </>
            )}
          </>
        }
        image={{
          alt: cardData.title,
          src:
            cardData.photos.length > 0
              ? (cardData.photos.find((photo) => photo.isPrimary) || cardData.photos[0])?.url
              : '',
        }}
        info={[
          {
            heading: t(`common:rooms`),
            icon: 'rooms',
            text: cardData['rooms'],
          },
          { heading: t('common:bathrooms'), icon: 'bathroom', text: cardData.bathrooms },
          {
            heading: t('common:status'),
            text: t(`common:${cardData.stateOfMaintenance.toLowerCase()}`),
          },
        ]}
        isHighEfficiency={['a', 'aplus', 'b'].includes(cardData.energyRating?.toLowerCase())}
        isOwnedByCasavo={cardData.ownedByCasavo}
        labels={{
          highEfficienyLabel: '',
          outdoorSpaceLabel: '',
        }}
        price={formatListingSellingPrice(cardData.isPrivateNegotiation, cardData.price, t, lang)}
        title={cardData.title}
      />
    </div>
  )
}
